<template>
  <div class="aiw2-mode">
    <h1 class="aiw2-mode__title">Start writing with POP AI Writer</h1>
    <p class="aiw2-mode__subtitle">Choose your mode - instant and easy or fully customized.</p>

    <div class="aiw2-mode__grid">
      <button
        class="mode-card"
        :class="{ selected: selected === MODES.FAST }"
        @click="selectMode(MODES.FAST)"
      >
        <div class="mode-icon">⚡</div>
        <h3>Fast Draft</h3>
        <p>Use POP's recommended settings to generate the full page quickly. You can still edit the final result.</p>
        <span class="mode-start" @click.stop="start(MODES.FAST)">Start Fast Draft →</span>
      </button>

      <button
        class="mode-card"
        :class="{ selected: selected === MODES.GUIDED }"
        @click="selectMode(MODES.GUIDED)"
      >
        <div class="mode-icon">✎</div>
        <h3>Guided Premium</h3>
        <p>Review the title, structure, draft, SEO fixes, and final polish before publishing.</p>
        <span class="mode-start" @click.stop="start(MODES.GUIDED)">Start Guided Premium →</span>
      </button>
    </div>

    <div class="tip-banner">
      <div class="tip-banner-content">
        <div class="tip-banner-title">{{ tips[currentTip].title }}</div>
        <div class="tip-banner-text">{{ tips[currentTip].text }}</div>
      </div>
      <button
        v-if="tips[currentTip].btnText"
        class="tip-link-btn"
        @click="onTipCta"
      >
        <span class="play">▶</span> {{ tips[currentTip].btnText }}
      </button>
      <div class="tip-dots">
        <button
          v-for="(tip, i) in tips"
          :key="i"
          class="tip-dot"
          :class="{ active: i === currentTip }"
          :aria-label="`Tip ${i + 1}`"
          @click="currentTip = i"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
import { MODES } from "../constants";

export default {
  name: "AiwV2ModeSelect",
  props: {
    initialMode: { type: String, default: MODES.GUIDED },
  },
  data() {
    return {
      MODES,
      selected: this.initialMode || MODES.GUIDED,
      currentTip: 0,
      tips: [
        {
          title: "Tip: Before you use POP AI Writer",
          text: "You may want to clean up some important terms from the Content Brief. This improves AI readability and quality.",
          btnText: "How to clean up terms",
        },
        {
          title: "Tip: After you use POP AI Writer",
          text: "It is best to think of AI content, from any tool, as a good first draft. Keep in mind that you may need to manually tweak the content a bit for tone, voice, and optimization.",
        },
      ],
    };
  },
  methods: {
    selectMode(mode) {
      this.selected = mode;
      // The Continue CTA lives in the shared bottom bar (Index.vue), so report
      // the current selection up to the parent.
      this.$emit("change", mode);
    },
    start(mode) {
      // The in-card "Start …" CTA selects the mode AND advances to the next
      // step (setup), same as the bottom-bar Continue. The parent's
      // `onModeContinue` creates the run and moves on.
      this.selected = mode;
      this.$emit("change", mode);
      this.$emit("start", mode);
    },
    onTipCta() {
      // Same tutorial popup the legacy AI Writer uses for "clean up terms".
      this.$store.commit("setCurrentVideoTutorialToDisplay", {
        gifLink: "/static/ai_writer_clean_tutorial.gif",
        gifWidth: 1100,
        noWidthHeightConstraints: true,
      });
    },
  },
};
</script>

<style scoped>
.aiw2-mode { max-width: 820px; margin: 56px auto 0; padding: 0 20px 40px; text-align: center; font-family: "Mulish", system-ui, sans-serif; }
.aiw2-mode__title { font-size: 28px; font-weight: 700; letter-spacing: -0.015em; margin: 0 0 8px; color: var(--text, #052e33); }
.aiw2-mode__subtitle { color: var(--text-3, #6b7b7e); font-size: 14.5px; margin: 0 0 36px; }
.aiw2-mode__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; text-align: left; }
@media (max-width: 720px) { .aiw2-mode__grid { grid-template-columns: 1fr; } }
.mode-card {
  background: var(--surface, #fff);
  border: 1.5px solid var(--border, #e4e4e4);
  border-radius: var(--r-xl, 14px);
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  text-align: left;
  font-family: inherit;
}
.mode-card:hover {
  border-color: var(--pop-orange-tint, #ffd9c2);
  box-shadow: var(--shadow, 0 1px 2px rgba(15,20,25,.04), 0 6px 18px rgba(15,20,25,.04));
  transform: translateY(-1px);
}
.mode-card.selected {
  border-color: var(--pop-orange, #ff5700);
  background: linear-gradient(180deg, var(--pop-orange-soft, #ffeddd) 0%, #fff 70%);
}
.mode-icon {
  width: 40px; height: 40px; border-radius: 9px;
  background: var(--pop-blue-tint, #e9f3ff); color: var(--pop-blue, #316bff);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; margin-bottom: 14px;
}
.mode-card.selected .mode-icon { background: var(--pop-orange, #ff5700); color: #fff; }
.mode-card h3 { font-size: 18px; font-weight: 700; margin: 0 0 6px; color: var(--text, #052e33); }
.mode-card p { color: var(--text-3, #6b7b7e); font-size: 13.5px; margin: 0 0 18px; line-height: 1.55; }
.mode-start {
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent;
  border: 1px solid var(--border-strong, #ccc);
  color: var(--text, #052e33);
  padding: 8px 16px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.mode-card.selected .mode-start { background: var(--pop-orange, #ff5700); border-color: var(--pop-orange, #ff5700); color: #fff; }
.mode-card.selected .mode-start:hover { background: var(--pop-orange-hover, #e64e00); }
/* Tip banner — mirrors #tipBanner from the design handoff (new design tokens). */
.tip-banner {
  margin-top: 24px;
  background: var(--pop-blue-tint, #e9f3ff);
  border: 1px solid var(--pop-blue-soft, #c7ddff);
  border-radius: var(--r-lg, 10px);
  padding: 14px 18px;
  display: flex; align-items: center; gap: 14px;
  text-align: left;
}
.tip-banner-content { flex: 1; }
.tip-banner-title { font-weight: 700; font-size: 13.5px; margin-bottom: 2px; color: var(--text, #052e33); }
.tip-banner-text { font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.tip-link-btn {
  background: #fff;
  border: 1px solid var(--border-strong, #ccc);
  padding: 7px 12px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text, #052e33);
  cursor: pointer;
  font-family: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  flex-shrink: 0;
}
.tip-link-btn .play {
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 1px solid currentColor;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 7px;
}
.tip-dots { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
.tip-dot {
  width: 8px; height: 8px; border-radius: 50%;
  border: none; padding: 0;
  background: var(--border-strong, #ccc);
  cursor: pointer; transition: all 0.18s;
}
.tip-dot:hover { background: var(--pop-orange, #ff5700); }
.tip-dot.active { background: var(--pop-orange, #ff5700); width: 18px; border-radius: 999px; }
</style>
